import {
  parseTemplateString,
  TemplateStringType,
} from "@turo/ui/lib/text/TemplateStringParser";
import PropTypes from "prop-types";

import { message as MessagePropertyType } from "./propTypes";

/**
 * Looks for functions as parameters. This indicates a translation contains
 * templates
 * @param {Object} parameters
 * @return {Boolean}
 */
const hasTemplateParameters = (parameters) =>
  parameters &&
  Object.values(parameters).some((value) => typeof value === "function");

/**
 * Builds the elements out of a template translation
 * @param {String} template
 * @param {Object} parameters
 * @return {Array}
 */
const renderTemplatedTranslation = (template, parameters) =>
  parseTemplateString(template).map(({ tag, text, type }) => {
    if (type === TemplateStringType.TYPE_TEXT) {
      return text;
    }

    const renderFunction = parameters[tag];
    const isFunction = typeof renderFunction === "function";
    return isFunction ? renderFunction(text) : text;
  });

/**
 * This component avoids the need to use `translate` calls directly.
 * It accepts many different param combinations:
 * - Scenario 1: message as an object with text
 * - Scenario 2: message as an object with a translation key
 * - Scenario 3: message as an object with a translation key and params
 * - Scenario 4: message as a translation key
 * - Scenario 5: message as an already translated text
 * - Scenario 6: message is an array for pluralization (e.g. )
 * Example:
 * ```
 * <TextMessage message={['You have only {count} car', 'You have {count} cars', 'count']} params={{count: 4}} />
 * ```
 * - Scenario 7: message as a translation key with params
 * @return {JSX.Element}
 */
export default function TextMessage({ message, params }, { t: translate }) {
  const {
    key: messageKey,
    message: messageText = "",
    params: messageParameters,
  } = message || {};
  const isMessagePluralizationArray =
    Array.isArray(message) && message.length === 3;

  // Message is not an object
  if (typeof message === "string") {
    // Scenario 1: template params are specified
    if (hasTemplateParameters(params)) {
      return renderTemplatedTranslation(translate(message), params);
    }

    // Scenario 2: the message was a translation key or a text. If it was a text, translate yields the same result
    return translate(message, params);
  }

  // Message is an object
  // Scenario 3: message has template params
  if (hasTemplateParameters(messageParameters)) {
    return renderTemplatedTranslation(translate(messageKey), messageParameters);
  }

  // Scenario 4: message has a translation key
  if (messageKey) {
    return translate(messageKey, messageParameters);
  }

  // Scenario 5: The message passed is a number for display. Turn it into a string and return it.
  if (typeof message === "number") {
    return String(message);
  }

  // Scenario 6: message is an array for pluralization
  if (isMessagePluralizationArray) {
    return translate(message, params);
  }

  // Scenario 7: message has no key so it's already translated
  return messageText;
}

TextMessage.contextTypes = {
  t: PropTypes.func.isRequired,
};

TextMessage.propTypes = {
  message: MessagePropertyType.isRequired,
  params: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.func,
      PropTypes.node,
    ])
  ),
};

TextMessage.defaultProps = {
  params: undefined,
};
